import { Tank, Vat, WaterMeter, WaterMonitor, WaterTank, FeedTank } from '@/api';
import { EntityStatus } from '@/api';

/**
 * @param type Entity or Product level alert
 * @param status The status of the entity or product, 'live', 'pending' etc
 * @param extra Certain products/entities have specific business rules that require further explanation
 * @returns Detailed information on what is needed to make a product or entity progress status
 */
export function getAlertText(
	type: 'product' | 'entity',
	status: EntityStatus,
	extra?: string,
): string {
	switch (status) {
		case 'draft':
			return `This ${type} is in draft status. Check the 'General' tab of the product. If the 'Draft Order Status' is 'In Progress' then you the customer is in the process of placing the order. You must wait until this is completed. If the 'Draft Order Status' is 'Confirmed' the order is awaiting review from Levno Customer Support. The CS team will call the customer to check the order is correct, and then confirm the order using the 'Confirm' button on the entity.`;
		case 'pending':
			return `This ${type} is in pending status. The order has been confirmed by the Levno Customer Support as valid, by calling the customer. It is now waiting for an install job to be scheduled and in doing so by clicking the 'Create Install Job' button, will move the status to pending.`;
		case 'install':
			const returnString = `This ${type} is in install status. The ${type} has an install job scheduled and is awaiting Levno Field Service visiting the location and attaching hardware. Once provisioning (Giving required information about the product/entity, like size, formula, flow rate, offset etc.) and installation (Attaching the transceiver and sensors to the ${type}) are completed, click the 'Move To Sign Off' button.`;
			switch (extra) {
				case 'water':
					return `${returnString} Entity location must be set and a correction must be set in this step.`;
				default:
					return returnString;
			}
		case 'sign-off':
			return `This ${type} is in sign-off status. The Levno Field Service has recently visited and installed the hardware. It is awaiting the Levno Customer Support team checking over after install, and verifying that everything is working as expected. To move to Live, click the 'Sign Off Entity' button.`;
		case 'removed':
			return `This ${type} is in removed status. This ${type} was previously installed and live, but has been removed.`;
		case 'cancelled':
			return `This ${type} is in cancelled status. The ${type} was previously in state that wasn't live, but was cancelled before installation was complete.`;
		case 'live':
		default:
			return '';
	}
}

enum ProductAction {
	Fault = 'fault',
	Repair = 'repair',
	Removal = 'removal',
	Install = 'install',
}

/**
 * @param type Type of actions occurring from product buttons, commonly a type of job
 * @returns Detailed information on what is needed to make a product button not disabled or why it is
 */
export function getProductActionText(type: ProductAction): string {
	switch (type) {
		case ProductAction.Fault:
			return 'Product status must not be draft.';
		case ProductAction.Repair:
			return 'Product must have a fault, must not have a job already and status must not be draft, cancelled or repair.';
		case ProductAction.Removal:
			return 'Product status must be live or sign off and must not be draft.';
		case ProductAction.Install:
		default:
			return 'Product status must be pending and must not be draft.';
	}
}

enum GeneralAction {
	Billing = 'billing',
	Trial = 'trial',
	Plan = 'plan',
	Org = 'org',
}

/**
 * @param type Type of actions occurring from buttons on the general tab
 * @returns Detailed information on what is needed to make a button not disabled or why it is
 */
export function getGeneralActionText(type: GeneralAction): string {
	switch (type) {
		case GeneralAction.Billing:
			return 'Product must exist and status must not be draft to edit billing.';
		case GeneralAction.Trial:
			return 'Product status must not be draft and product must not have already had a trial.';
		case GeneralAction.Plan:
			return 'Product status must not be draft.';
		case GeneralAction.Org:
			return 'Product billing must be cleared. If urgent, contact Software Team.';
		default:
			return 'Unknown Action';
	}
}

enum OtherAction {
	AddUser = 'add-user',
	FuelCollectionsMatch = 'fuel-collections-match',
	FuelCollectionsEdit = 'fuel-collections-edit',
	FuelCollectionsAdd = 'fuel-collections-add',
	MilkProvision = 'milk-provision',
}

/**
 * @param type Type of action occurring from button
 * @returns Detailed information on why a button is disabled
 */
export function getOtherActionText(type: OtherAction): string {
	switch (type) {
		case OtherAction.AddUser:
			return 'User cap has been reached (or is not set).';
		case OtherAction.FuelCollectionsMatch:
			return 'There must be valid dips and/or refills available to apply height dip.';
		case OtherAction.FuelCollectionsEdit:
			return 'There are no dips or refills';
		case OtherAction.FuelCollectionsAdd:
			return 'There must be data available to add to list and the amount input must be set.';
		case OtherAction.MilkProvision:
			return 'Formula, vat size, and vat offset must be set in order to calculate empty distance. Default can only be set if the current value is not already default.';
		default:
			return 'Unknown Action';
	}
}

// Internal function to extend getEntityButtonsText
function getEntityButtonsExtraText(type: EntityAction, extra: string): string {
	switch (extra) {
		case 'water-install':
			return `${getEntityButtonsText(
				type,
			)} Entity location must be set and a correction must be set to be able to access the ${type} modal.`;
		default:
			return 'Unknown Action';
	}
}

enum EntityAction {
	Confirm = 'confirm',
	ToSignOff = 'to-sign-off',
	FromSignOff = 'from-sign-off',
	Install = 'install',
	Provision = 'provision',
	Hardware = 'hardware',
	Edit = 'edit',
	Remove = 'remove',
	Cancel = 'cancel',
}

/**
 * @param type Type of action occurring from an entity button
 * @param extra Certain examples have specific business cases that need addition explanation
 * @returns Detailed information on what is needed to make a entity button not disabled
 */
export function getEntityButtonsText(type: EntityAction, extra?: string): string {
	if (extra) return getEntityButtonsExtraText(type, extra);
	switch (type) {
		case EntityAction.Confirm:
			return 'Product order status must not be in progress. The user must completed their order before Levno Customer Support is able to confirm the order.';
		case EntityAction.ToSignOff:
			return 'Product status must be install. To do so, provisioning and installation must be complete.';
		case EntityAction.FromSignOff:
			return "Product status must be sign off. To get to sign off status, the 'Move Entity To Sign Off' button must be pressed.";
		case EntityAction.Install:
			return 'Product status must be install. To get to install status this product must in pending status and reviewed by Levno.';
		case EntityAction.Provision:
			return 'Product status must be install to be able to provision. To get to install status this product must in pending status and reviewed by Levno.';
		case EntityAction.Hardware:
			return 'Product status must be live or sign off to be able to change hardware.';
		case EntityAction.Edit:
			return 'Product status must be live, sign off, or draft to be able to edit.';
		case EntityAction.Remove:
			return 'Product status must be live or sign off to be able to remove.';
		case EntityAction.Cancel:
			return 'Product status must be install or pending to be able to cancel.';
		default:
			return '';
	}
}

type Entity = Vat | Tank | WaterTank | WaterMeter | WaterMonitor | FeedTank;

export function showHelpTexts(entity: string | Entity | undefined | null): boolean {
	if (!entity) return false;
	if (typeof entity === 'string') {
		return entity !== 'live';
	} else {
		return entity.status !== 'live';
	}
}
